import "./Search.css";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import { Tabs, Tab, TextField, MenuItem } from "@mui/material";
import useWindowDimensions from "@mirage/hooks/useWindowDimensions";
import { PlanningAppealsSearch } from "./PlanningAppeal/PlanningAppealsSearch";
import EnforcementNoticeSearch from "./EnforcementNotice/EnforcementNoticeSearch";
import EnforcementAppealsSearch from "./EnforcementAppeal/EnforcementAppealsSearch";
import PlanningApplicationSearch from "./PlanningApplication/PlanningApplicationSearch";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import { WeeklyListsSearch } from "./WeeklyLists/WeeklyListsSearch";
import { atom, useRecoilState } from "recoil";
import { TabNames } from "@publicPortal/utils/enums";
import ErrorBoundary from "@mirage/components/errorBoundary/ErrorBoundary";
import BuildingRegulationsSearch from "./BuildingRegulations/BuildingRegulationsSearch";
import PropertySearch from "./PropertySearch/PropertySearch";
import { DrawProps } from "@mirage/components/MapView/components/GeomanControl";
import { useLocation } from "react-router-dom";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";

const searchTabsState = atom({
    key: "searchTabsState",
    default: TabNames["Property Search"],
});
export const PublicPortalDrawProps: DrawProps = { drawPolygon: true, editMode: true, cutPolygon: true, dragMode: true, removalMode: true };
const Search = () => {
    const [currentCard, setCurrentCard] = useRecoilState(searchTabsState);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { width } = useWindowDimensions();
    const location = useLocation();

    const getCardPicker = () => {
        if (width <= PublicPortalTheme.breakpoints.values.sm) {
            return getMobileTabPicker();
        }
        return getDefaultTabs();
    };
    return (
        <>
            <ResponsiveAppBar />
            <Board size="big">
                {getCardPicker()}
                <ErrorBoundary key={location.pathname}>
                    <section className="search-form">{getTabContent()}</section>
                </ErrorBoundary>
            </Board>
        </>
    );

    function getTabContent() {
        switch (currentCard) {
            case TabNames["Planning Applications"]:
                return <PlanningApplicationSearch />;
            case TabNames["Planning Appeals"]:
                return <PlanningAppealsSearch />;
            case TabNames["Weekly Lists"]:
                return <WeeklyListsSearch />;
            case TabNames["Enforcement Notices"]:
                return <EnforcementNoticeSearch />;
            case TabNames["Enforcement Appeals"]:
                return <EnforcementAppealsSearch />;
            case TabNames["Building Regulations"]:
                return <BuildingRegulationsSearch />;
            case TabNames["Property Search"]:
                return <PropertySearch />;
            default:
                throw Error(`Wrong Tab Name on getTabContent - ${currentCard}`);
        }
    }

    function getDefaultTabs() {
        const tabsJustify = width < PublicPortalTheme.breakpoints.values.lg ? "normal" : "space-evenly";
        return (
            <Tabs
                value={currentCard}
                onChange={(event, newTab: TabNames) => setCurrentCard(newTab)}
                aria-label="nav bar"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ marginBottom: "1rem", "& .MuiTabs-flexContainer": { justifyContent: tabsJustify } }}
            >
                <Tab className="searchTab" disableRipple label={TabNames["Property Search"]} value={TabNames["Property Search"]} />
                {sessionValue.clientSettings?.BUILDING_REGULATIONS ? (
                    <Tab className="searchTab" disableRipple label={TabNames["Building Regulations"]} value={TabNames["Building Regulations"]} />
                ) : null}
                {sessionValue.clientSettings?.PLANNING_APPLICATIONS ? (
                    <Tab className="searchTab" disableRipple label={TabNames["Planning Applications"]} value={TabNames["Planning Applications"]} />
                ) : null}
                {sessionValue.clientSettings?.PLANNING_APPEALS ? (
                    <Tab className="searchTab" disableRipple label={TabNames["Planning Appeals"]} value={TabNames["Planning Appeals"]} />
                ) : null}
                {sessionValue.clientSettings?.WEEKLY_LISTS ? <Tab className="searchTab" disableRipple label={TabNames["Weekly Lists"]} value={TabNames["Weekly Lists"]} /> : null}
                {sessionValue.clientSettings?.ENFORCEMENT_NOTICES ? (
                    <Tab className="searchTab" disableRipple label={TabNames["Enforcement Notices"]} value={TabNames["Enforcement Notices"]} />
                ) : null}
                {sessionValue.clientSettings?.ENFORCEMENT_APPEALS ? (
                    <Tab className="searchTab" disableRipple label={TabNames["Enforcement Appeals"]} value={TabNames["Enforcement Appeals"]} />
                ) : null}
            </Tabs>
        );
    }

    function getMobileTabPicker() {
        return (
            <TextField
                className="search-by-card-picklist-field"
                id="search-searchType"
                value={currentCard}
                onChange={(event) => setCurrentCard(event.target.value as TabNames)}
                label="Search By"
                variant="outlined"
                select
            >
                {Object.values(TabNames).map((title) => {
                    return (
                        <MenuItem key={title} value={title}>
                            {title}
                        </MenuItem>
                    );
                })}
            </TextField>
        );
    }
};

export default Search;
